import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import type { Rights } from '../lib/session.js';
import { EXCEPTION_LABEL, label } from '../lib/labels.js';
import { dateTime, num } from '../lib/format.js';
import {
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import '../screens-2a.css';
import { hrefFor } from '../lib/router.js';

interface ExceptionRow {
  id: number;
  type: string;
  status: string;
  note: string | null;
  created_at: string;
  resolved_at: string | null;
  contact_id: string | null;
  race_id: string | null;
  customer_id: string | null;
  race_name: string | null;
  assignee_email: string | null;
  assignee_name: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  open: 'Otevřená',
  in_progress: 'Řeší se',
  resolved: 'Vyřešená',
};

export function Exceptions({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const [status, setStatus] = useState('open');
  const [type, setType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<Record<number, string>>({});

  const query = useQuery<{
    items: ExceptionRow[];
    openByType: Array<{ type: string; n: number }>;
  }>({
    queryKey: ['exceptions', status, type],
    queryFn: () => api.get(`/api/exceptions${qs({ status, type })}`),
  });

  const update = useMutation({
    mutationFn: (input: { id: number; status?: string; note?: string; assignToMe?: boolean }) =>
      api.patch(`/api/exceptions/${input.id}`, input),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['exceptions'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (query.isLoading) return <LoadingState what="výjimky" />;
  if (query.isError)
    return (
      <ErrorState
        title="Výjimky se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );

  const canWrite = rights.write('exceptions');

  return (
    <>
      <PageHeader
        title="Výjimky"
        description="Fronta situací, které hub sám nevyřeší a potřebuje k nim člověka, například chybějící šablona nebo nepotvrzené odeslání. Každá výjimka je nedoručená zpráva, proto si hlídejte ty starší než pár dní."
      />

      <div className="s2a-filters">
        <Field label="Stav">
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="open">Otevřené</option>
            <option value="in_progress">Řeší se</option>
            <option value="resolved">Vyřešené</option>
            <option value="">Všechny</option>
          </select>
        </Field>
        <Field label="Typ výjimky">
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Všechny typy</option>
            {Object.entries(EXCEPTION_LABEL).map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {error ? <ErrorState title="Změnu se nepovedlo uložit" error={new Error(error)} /> : null}

      {query.data && query.data.openByType.length > 0 ? (
        <div className="row s2a-mb" aria-label="Otevřené výjimky podle typu">
          {query.data.openByType.map((t) => (
            <button
              key={t.type}
              className="btn"
              aria-pressed={type === t.type}
              onClick={() => setType(t.type)}
            >
              {label(EXCEPTION_LABEL, t.type)} <b>{num(t.n)}</b>
            </button>
          ))}
        </div>
      ) : null}

      <Panel title={`Fronta (${num(query.data?.items.length ?? 0)})`}>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Typ</th>
                <th>Co se stalo</th>
                <th>Kdo řeší</th>
                <th>Stav</th>
                <th>Vzniklo</th>
                {canWrite ? <th>Akce</th> : null}
              </tr>
            </thead>
            <tbody>
              {query.data?.items.map((e) => (
                <tr key={e.id}>
                  <td>{label(EXCEPTION_LABEL, e.type)}</td>
                  <td>
                    {e.note ?? '—'}
                    <div className="muted">
                      {e.contact_id ? (
                        <a href={hrefFor('customers', e.contact_id)}>
                          {e.customer_id ?? 'zákazník'}
                        </a>
                      ) : null}
                      {e.race_id ? (
                        <>
                          {e.contact_id ? ' · ' : ''}
                          <a href={hrefFor('races', e.race_id)}>{e.race_name ?? e.race_id}</a>
                        </>
                      ) : null}
                    </div>
                  </td>
                  <td>{e.assignee_name ?? <span className="muted">nikdo</span>}</td>
                  <td>
                    <StatusBadge
                      tone={e.status === 'open' ? 'warn' : e.status === 'resolved' ? 'ok' : 'acc'}
                      icon={
                        e.status === 'open' ? 'alert' : e.status === 'resolved' ? 'check' : 'clock'
                      }
                    >
                      {label(STATUS_LABEL, e.status)}
                    </StatusBadge>
                  </td>
                  <td>{dateTime(e.created_at)}</td>
                  {canWrite ? (
                    <td>
                      {e.status === 'resolved' ? (
                        <span className="muted">{dateTime(e.resolved_at)}</span>
                      ) : (
                        <div className="row">
                          {!e.assignee_email ? (
                            <button
                              className="btn"
                              onClick={() =>
                                update.mutate({ id: e.id, assignToMe: true, status: 'in_progress' })
                              }
                            >
                              Vezmu si to
                            </button>
                          ) : null}
                          <input
                            type="text"
                            placeholder="Poznámka"
                            aria-label={`Poznámka k výjimce ${e.id}`}
                            value={note[e.id] ?? ''}
                            onChange={(ev) => setNote({ ...note, [e.id]: ev.target.value })}
                          />
                          <button
                            className="btn"
                            onClick={() =>
                              update.mutate({
                                id: e.id,
                                status: 'resolved',
                                ...(note[e.id] ? { note: note[e.id] } : {}),
                              })
                            }
                          >
                            Vyřešeno
                          </button>
                        </div>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {query.data && query.data.items.length === 0 ? (
          <EmptyState
            title={status === 'open' ? 'Nic nečeká na člověka' : 'Žádné výjimky podle filtru'}
          >
            {status === 'open'
              ? 'Hub zatím všechno zvládl sám. Až narazí na něco, co nevyřeší, objeví se to tady.'
              : 'Zkuste změnit stav nebo typ výjimky výše.'}
          </EmptyState>
        ) : null}
      </Panel>
    </>
  );
}
